/**
 * KeyFindingsCard - Narrative summary of simulation results
 * with generateNarrative utility function.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Lightbulb as InsightIcon } from '@mui/icons-material';
import { vortxTypography } from '@vortx/shared-ui';
import { COLORS, fadeInUp } from './ResultsDesignTokens';

// Generate plain-English interpretation of simulation results
export const generateNarrative = (result, topology, isComparison = false, comparisonData = null) => {
    if (isComparison && comparisonData) {
        const { results, recommendation, efficiency_gain_pct } = comparisonData;
        const winner = results?.find(r => r.topology_id === recommendation);
        const loser = results?.find(r => r.topology_id !== recommendation);

        if (!winner || !loser) return null;

        const energySaved = Math.abs((winner.total_losses_kwh || 0) - (loser.total_losses_kwh || 0));
        const winnerIsDC = recommendation >= 2 && recommendation <= 4;

        let reason = '';
        if (winnerIsDC) {
            reason = 'By eliminating AC/DC conversion steps between the PV and battery, it reduces inverter losses during charging.';
        } else if (recommendation === 1) {
            reason = 'The simpler AC-coupled design has lower component count, reducing potential failure points.';
        } else {
            reason = 'The hybrid configuration optimizes power routing based on instantaneous demand.';
        }

        return {
            headline: `Topology #${recommendation} wins`,
            summary: `The **${winner.topology_name || 'winning topology'}** achieved **${winner.efficiency_pct?.toFixed(1)}%** efficiency, outperforming the alternative by **${efficiency_gain_pct?.toFixed(2)}%**.`,
            detail: `${reason} This saved approximately **${energySaved.toFixed(0)} kWh** in losses over the simulation period.`,
            insight: `The efficiency difference is most pronounced during ${winnerIsDC ? 'solar charging hours when DC-coupled avoids double conversion' : 'evening discharge when grid-tied inverters operate at higher loads'}.`,
        };
    }

    // Single topology narrative
    if (!result) return null;

    const efficiency = result.efficiency_pct ?? result.summary?.net_efficiency_pct ?? 0;
    const totalLoss = result.total_losses_kwh ?? result.summary?.total_loss_kwh ?? 0;
    const totalSolar = result.total_solar_kwh ?? result.summary?.total_solar_kwh ?? 0;
    const totalEv = result.total_ev_kwh ?? 0;
    const totalBessCharge = result.total_bess_charge_kwh ?? 0;
    const totalBessDischarge = result.total_bess_discharge_kwh ?? 0;
    const gridImport = result.total_grid_import_kwh ?? 0;

    // Determine primary power source
    const solarFraction = totalSolar / (totalSolar + gridImport + 0.01);
    const primarySource = solarFraction > 0.6 ? 'solar' : solarFraction > 0.3 ? 'mixed solar/grid' : 'grid';

    // Identify loss mechanism
    let lossReason = 'conversion losses';
    if (totalBessCharge > totalSolar * 0.3) {
        lossReason = 'battery charge/discharge cycles';
    } else if (gridImport > totalSolar * 0.5) {
        lossReason = 'grid-tied inverter efficiency at partial loads';
    }

    const isDCCoupled = topology?.id >= 2 && topology?.id <= 4;

    return {
        headline: `${efficiency.toFixed(1)}% System Efficiency`,
        summary: `This ${isDCCoupled ? 'DC-coupled' : 'AC-coupled'} topology achieved **${efficiency.toFixed(1)}% round-trip efficiency** over the 24-hour simulation, primarily powered by ${primarySource}.`,
        detail: `The system delivered **${totalEv.toFixed(0)} kWh** to EV charging while losing **${totalLoss.toFixed(1)} kWh** to ${lossReason}.`,
        insight: totalBessCharge > 0
            ? `Battery storage cycled **${totalBessCharge.toFixed(0)} kWh** in and **${totalBessDischarge.toFixed(0)} kWh** out, providing load shifting capability.`
            : `Direct solar-to-EV power transfer maximized efficiency by minimizing storage cycles.`,
    };
};

// Key Findings Card - prominent narrative summary
export const KeyFindingsCard = ({ result, topology, isComparison = false, comparisonData = null }) => {
    const narrative = generateNarrative(result, topology, isComparison, comparisonData);

    if (!narrative) return null;

    // Helper to render text with **bold** markdown
    const renderBoldText = (text) => {
        const parts = text.split(/\*\*(.*?)\*\*/g);
        return parts.map((part, i) =>
            i % 2 === 1 ? (
                <Typography key={i} component="span" sx={{
                    fontWeight: 700,
                    color: COLORS.efficiency,
                    textShadow: `0 0 10px ${alpha(COLORS.efficiency, 0.3)}`,
                }}>
                    {part}
                </Typography>
            ) : part
        );
    };

    return (
        <Box sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(COLORS.efficiency, 0.08)} 0%, ${alpha(COLORS.accent, 0.08)} 100%)`,
            border: `1px solid ${alpha(COLORS.efficiency, 0.25)}`,
            position: 'relative',
            overflow: 'hidden',
            animation: `${fadeInUp} 0.6s ease-out`,
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 4,
                height: '100%',
                background: `linear-gradient(180deg, ${COLORS.efficiency}, ${COLORS.accent})`,
                boxShadow: `0 0 15px ${alpha(COLORS.efficiency, 0.5)}`,
            },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: alpha(COLORS.efficiency, 0.15),
                    border: `1px solid ${alpha(COLORS.efficiency, 0.3)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <InsightIcon sx={{ color: COLORS.efficiency, fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: COLORS.text.muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        mb: 0.5,
                    }}>
                        Key Findings
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: COLORS.efficiency,
                        textShadow: `0 0 20px ${alpha(COLORS.efficiency, 0.4)}`,
                        mb: 1.5,
                    }}>
                        {narrative.headline}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.85rem',
                        color: COLORS.text.primary,
                        lineHeight: 1.7,
                        mb: 1,
                    }}>
                        {renderBoldText(narrative.summary)}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.8rem',
                        color: COLORS.text.secondary,
                        lineHeight: 1.6,
                        mb: 1,
                    }}>
                        {renderBoldText(narrative.detail)}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: COLORS.text.muted,
                        lineHeight: 1.6,
                        fontStyle: 'italic',
                    }}>
                        {renderBoldText(narrative.insight)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
